import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthGate } from "@/components/AuthGate";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { listReviews } from "@/lib/reviews.functions";
import { getMyProfile } from "@/lib/institutions.functions";
import { Button } from "@/components/ui/button";
import { Building2, Check, Sparkles, ArrowRight, User } from "lucide-react";
import { STARTERS } from "@/lib/starters";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading } from "@/components/editorial";
import { StudentProgress } from "@/components/StudentProgress";
import { DirectoryPrompt } from "@/components/DirectoryPrompt";

export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [
      { title: "Your dashboard — Tiger's Eye" },
      { name: "description", content: "Your Tiger's Eye dashboard: start a new review or revisit a recent critique." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (<AuthGate><Dashboard /></AuthGate>),
});

function Dashboard() {
  const { user } = useAuth();
  const fetchReviews = useServerFn(listReviews);
  const profileFn = useServerFn(getMyProfile);
  const { data, isLoading } = useQuery({
    queryKey: ["reviews"],
    queryFn: () => fetchReviews(),
  });
  const { data: profData } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => profileFn(),
  });

  const hasName = !!(profData?.profile?.preferred_name && profData.profile.preferred_name.trim());
  const hasInstitution = !!(profData?.profile?.institution_id || profData?.profile?.institution_name);
  const setupComplete = hasName && hasInstitution;
  const reviewCount = data?.reviews.length ?? 0;
  const isFirstSession = setupComplete && reviewCount === 0;
  const stepsDone = (hasName ? 1 : 0) + (hasInstitution ? 1 : 0);

  const name =
    profData?.profile?.preferred_name ??
    profData?.profile?.display_name ??
    (user?.user_metadata?.full_name as string | undefined) ??
    (user?.user_metadata?.display_name as string | undefined) ??
    user?.email?.split("@")[0] ??
    "there";

  return (
    <PageContainer className="min-h-[60vh]">
      <div className="flex items-center gap-3 mb-3">
        <Eyebrow>Welcome{setupComplete ? " back" : ""}, <span className="capitalize">{name}</span></Eyebrow>
        {hasInstitution && (
          <span className="inline-flex items-center gap-1 text-[0.65rem] font-mono tracking-widest border border-rule text-primary px-2 py-0.5">
            <Building2 className="h-3 w-3" />
            {profData!.profile!.institution_name}
          </span>
        )}
      </div>
      <DisplayHeading level={1}>Your dashboard</DisplayHeading>
      <p className="mt-3 text-ink-secondary">
        {setupComplete
          ? "Begin a new review, or open a recent one to revisit the critique."
          : "Welcome to Tiger's Eye. A couple of quick details and you're ready to go."}
      </p>

      {hasInstitution && setupComplete && (
        <p className="mt-3 text-xs text-ink-tertiary">
          If you are not still at {profData!.profile!.institution_name}, please choose the right one when you run your next review.
        </p>
      )}

      {!setupComplete && profData?.profile && (
        <div className="mt-8 border border-primary/40 bg-primary/5 p-6 rounded-md animate-fade-in">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <Eyebrow className="mb-1">Finish setting up</Eyebrow>
              <DisplayHeading level={2} className="text-2xl">Two small steps before your first review</DisplayHeading>
            </div>
            <span className="text-xs font-mono tracking-wider text-ink-tertiary whitespace-nowrap">
              {stepsDone} / 2 DONE
            </span>
          </div>
          <div className="h-1.5 w-full bg-border rounded-full overflow-hidden mb-5">
            <div className="h-full bg-primary transition-all" style={{ width: `${(stepsDone / 2) * 100}%` }} />
          </div>
          <ul className="space-y-2.5 mb-5 text-sm">
            <OnboardingStep done={hasName} icon={<User className="h-3.5 w-3.5" />} label="Tell us your preferred name" hint="So feedback feels personal." />
            <OnboardingStep done={hasInstitution} icon={<Building2 className="h-3.5 w-3.5" />} label="Choose your institution" hint="We tailor referencing rules and level expectations to it." />
          </ul>
          <Link to="/profile">
            <Button>
              {stepsDone === 0 ? "Start setup" : "Continue setup"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      )}

      {isFirstSession && (
        <div className="mt-8 border border-primary/40 bg-card p-6 rounded-md animate-fade-in">
          <div className="flex items-start gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <Eyebrow className="mb-1">You're all set</Eyebrow>
              <DisplayHeading level={2} className="text-2xl mb-1">Start your first review</DisplayHeading>
              <p className="text-sm text-ink-secondary mb-4">
                Upload a draft, paste an assignment brief, or pick a prompt below. Tiger's Eye reads carefully and
                gives you a calm, editorial critique — never a grade, never a rewrite.
              </p>
              <Link to="/reviews/new">
                <Button size="lg">Start your first review <ArrowRight className="h-4 w-4" /></Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/reviews/new"><Button size="lg">Start a new review</Button></Link>
        <Link to="/reviews"><Button size="lg" variant="outline">All reviews</Button></Link>
      </div>

      <DirectoryPrompt />

      <section className="mt-14">
        <Eyebrow as="h2" className="mb-3">Begin with a question</Eyebrow>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {STARTERS.map((s) => (
            <Link
              key={s.id}
              to="/reviews/new"
              search={{ starter: s.id }}
              className="block border border-rule bg-card p-4 text-sm text-ink-secondary hover:border-primary transition-colors"
            >
              <span className="font-mono text-xs text-ink-tertiary mr-2">›</span>{s.label}
            </Link>
          ))}
        </div>
      </section>

      <StudentProgress />
    </PageContainer>
  );
}

function OnboardingStep({
  done,
  icon,
  label,
  hint,
}: {
  done: boolean;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border shrink-0 ${
          done
            ? "bg-primary text-primary-foreground border-primary"
            : "border-border text-ink-tertiary bg-background"
        }`}
      >
        {done ? <Check className="h-3.5 w-3.5" /> : icon}
      </span>
      <span className="flex-1">
        <span className={done ? "text-ink line-through" : "text-ink font-medium"}>{label}</span>
        <span className="block text-xs text-ink-tertiary mt-0.5">{hint}</span>
      </span>
    </li>
  );
}
